import styles from './TextHighlighter.module.scss';

export interface HighlightedText {
  start: number;
  end: number;
  className: string;
  paragraph: number;
}

interface TextHighlighterProps {
  newParagraphs: string[];
  highlights: HighlightedText[];
}

function TextHighlighter({newParagraphs, highlights}: TextHighlighterProps) {
  function renderHighlightedText(text: string, paragraphIndex: number) {
    const highlightMarkers: {
      position: number;
      type: 'start' | 'end';
      className: string;
    }[] = [];

    // Iterates over the highlights and adds start and end markers to the highlightMarkers array
    // only for highlights that match the specified paragraph index.
    highlights?.forEach(({start, end, className, paragraph}) => {
      if (paragraph === paragraphIndex) {
        // Adds the start marker (position 'start') of the highlight to the array
        highlightMarkers?.push({position: start, type: 'start', className});

        // Adds the end marker (position 'end') of the highlight to the array
        highlightMarkers?.push({position: end, type: 'end', className});
      }
    });

    // Sorts the highlight markers by their position. If two markers have the same position,
    // the 'start' marker will be placed before the 'end' marker, ensuring the correct highlight order.

    // The comparison works as follows:
    // 1. First, the difference between the markers' positions is calculated (startMarker.position - endMarker.position).
    //    - If the positions are different, the order is determined based on this difference.
    // 2. If the positions are equal (difference equals 0), the code checks the type of the marker.
    //    - If the marker is of type 'end', the comparison returns -1, placing the 'end' marker after.
    //    - If the marker is of type 'start', the comparison returns 1, placing the 'start' marker before.

    highlightMarkers?.sort(
      (startMarker, endMarker) =>
        startMarker.position - endMarker.position ||
        (startMarker.type === 'end' ? -1 : 1),
    );

    const highlightedText: (string | JSX.Element)[] = []; // Array with highlighted text or original text

    let activeClasses: string[] = []; // Array to keep track of active classes

    let currentTextIndex = 0; // Keeps track of the last processed position in the text

    let lastElementRef: JSX.Element | null = null; // Stores the last highlighted element with a reference

    highlightMarkers?.forEach(({position, type, className}, highlightIndex) => {
      // If there is text between the last processed index and the current position
      if (currentTextIndex < position) {
        highlightedText.push(
          activeClasses.length > 0 ? (
            // If there are active classes, wrap the text in a <span> with markers
            <span className={activeClasses.join(' ')}>
              {text?.slice(currentTextIndex, position)}
            </span>
          ) : (
            // Otherwise, just add original text
            text?.slice(currentTextIndex, position)
          ),
        );
      }

      // If the marker is of type 'start', add the class to activeClasses
      if (type === 'start') {
        activeClasses.push(className);
      } else {
        // If the marker is of type 'end', remove the class from activeClasses
        activeClasses = activeClasses.filter(
          (activeClass) => activeClass !== className,
        );
      }

      // Update the currentTextIndex to the current position for the next iteration
      currentTextIndex = position;

      // If this is the last marker, store a reference to the last highlighted text
      if (highlightIndex === highlightMarkers.length - 1) {
        lastElementRef = (
          <span className={activeClasses.join(' ')}>
            {text?.slice(position)}
          </span>
        );
      }
    });

    // After processing all highlight markers, check if there's any remaining text
    if (lastElementRef) {
      highlightedText.push(lastElementRef);
    } else if (currentTextIndex < text.length) {
      highlightedText.push(
        activeClasses.length > 0 ? (
          // If there are still active classes, wrap the remaining text in a <span> with those classes
          <span className={activeClasses.join(' ')}>
            {text?.slice(currentTextIndex)}
          </span>
        ) : (
          // Otherwise, just add original text
          text.slice(currentTextIndex)
        ),
      );
    }

    // Return the final result with text and highlights
    return highlightedText;
  }

  return (
    <div className={styles.container}>
      {newParagraphs.map((paragraph, index) => (
        <p key={`${paragraph}-${index}`}>
          {renderHighlightedText(paragraph, index)}
        </p>
      ))}
    </div>
  );
}

export default TextHighlighter;
